import { Eye, EyeOff, PencilLine, LogOut, Loader2 } from 'lucide-react'
import { useEffect, useRef, useState, type ChangeEvent } from 'react'
import toast from 'react-hot-toast'
import { Link, useNavigate } from 'react-router-dom'

import { DashboardLayout } from '../../components/layout/DashboardLayout'
import { controlIcons, shellAssets } from '../../constants/assets'
import { dashboardRoutes } from '../../constants/routes'
import { logoutAdmin } from '../../api/auth/adminAuthApi'
import { useConfirmDialog } from '../../context/ConfirmDialogContext'
import {
  apiGetProfile,
  apiUpdateProfile,
  apiUpdateAdminPassword,
  apiUploadProfilePhoto,
  apiUpdateLegalSettings,
  DEFAULT_PROFILE,
  type ProfileDetails,
} from '../../api/profile/profileApi'

const POLICY_COPY =
  'At SalzburgCab, we respect your privacy and are committed to protecting your personal information. We may collect information such as your name, email address, phone number, pickup and drop-off locations, travel dates and times, flight or train details, passenger information, luggage details, special requests, and payment-related information when you use our services. This information is used to process and manage bookings, arrange transportation, communicate booking updates, provide customer support, process payments, improve our services, and meet legal requirements. We may share necessary information with assigned drivers, transportation partners, payment providers, and trusted service providers to complete your booking. We take reasonable measures to protect your information from unauthorized access, misuse, or disclosure and retain it only for as long as necessary for business and legal purposes. By using SalzburgCab services, you agree to the collection and use of your information as described in this Privacy Policy.'

const defaultAvatar = '/assets/dashboard/profile/profile-avatar.png'

const ProfileSettingsPage = () => {
  const navigate = useNavigate()
  const [profile, setProfile] = useState<ProfileDetails>(DEFAULT_PROFILE)
  const [draft, setDraft] = useState<ProfileDetails>(DEFAULT_PROFILE)
  const [profileEditing, setProfileEditing] = useState(false)
  const [photo, setPhoto] = useState(defaultAvatar)
  const [loading, setLoading] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [passwordLoading, setPasswordLoading] = useState(false)
  const [showCurrent, setShowCurrent] = useState(false)
  const [showNew, setShowNew] = useState(false)
  const [showConfirm, setShowConfirm] = useState(false)

  const [privacy, setPrivacy] = useState(POLICY_COPY)
  const [privacyDraft, setPrivacyDraft] = useState(POLICY_COPY)
  const [privacyEditing, setPrivacyEditing] = useState(false)
  const [terms, setTerms] = useState(POLICY_COPY)
  const [termsDraft, setTermsDraft] = useState(POLICY_COPY)
  const [termsEditing, setTermsEditing] = useState(false)

  // Fetch profile details via API on mount
  useEffect(() => {
    let mounted = true
    const loadData = async () => {
      setLoading(true)
      try {
        const res = await apiGetProfile()
        if (mounted && res.success && res.data) {
          setProfile(res.data)
          setDraft(res.data)
          if (res.data.avatar) setPhoto(res.data.avatar)
        } else if (mounted && !res.success) {
          toast.error(res.message || 'Unable to load profile')
          if (!localStorage.getItem('salzburg_admin_token')) {
            navigate('/admin-login', { replace: true })
          }
        }
      } catch {
        if (mounted) toast.error('Unable to load profile')
      } finally {
        if (mounted) setLoading(false)
      }
    }
    loadData()
    return () => {
      mounted = false
    }
  }, [])

  const handleProfileEdit = async () => {
    if (!profileEditing) {
      setDraft(profile)
      setProfileEditing(true)
      return
    }

    const cleaned: ProfileDetails = {
      firstName: draft.firstName.trim() || profile.firstName,
      lastName: draft.lastName.trim() || profile.lastName,
      email: draft.email.trim() || profile.email,
      phone: draft.phone.trim() || profile.phone,
      role: profile.role,
      country: draft.country.trim() || profile.country,
      avatar: photo,
    }

    setLoading(true)
    try {
      const res = await apiUpdateProfile(cleaned)
      if (res.success) {
        setProfile(cleaned)
        setDraft(cleaned)
        setProfileEditing(false)
        toast.success(res.message || 'Profile updated successfully!')
      } else {
        toast.error(res.message || 'Failed to update profile')
      }
    } catch {
      toast.error('Unable to update profile. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const handlePhoto = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return

    const preview = URL.createObjectURL(file)
    try {
      const result = await apiUploadProfilePhoto(file)
      if (!result.success) {
        toast.error(result.message || 'Failed to upload profile photo')
        return
      }
      setPhoto(result.data?.photoUrl || preview)
      toast.success(result.message || 'Profile photo updated!')
    } catch {
      toast.error('Failed to upload profile photo')
      URL.revokeObjectURL(preview)
    }
    event.target.value = ''
  }

  const updatePassword = async () => {
    if (!currentPassword.trim() || !newPassword.trim() || !confirmPassword.trim()) {
      toast.error('Please fill all password fields')
      return
    }
    if (newPassword !== confirmPassword) {
      toast.error('New passwords do not match')
      return
    }
    if (newPassword.length < 6) {
      toast.error('Password must be at least 6 characters long')
      return
    }

    setPasswordLoading(true)
    try {
      const res = await apiUpdateAdminPassword({
        currentPassword,
        newPassword,
        confirmPassword,
      })

      if (res.success) {
        setCurrentPassword('')
        setNewPassword('')
        setConfirmPassword('')
        toast.success(res.message || 'Password updated successfully!')
      } else {
        toast.error(res.message || 'Failed to update password')
      }
    } catch {
      toast.error('Connection failed while updating password')
    } finally {
      setPasswordLoading(false)
    }
  }

  const savePrivacy = async () => {
    if (!privacyEditing) {
      setPrivacyDraft(privacy)
      setPrivacyEditing(true)
      return
    }
    const value = privacyDraft.trim() || privacy
    setPrivacy(value)
    setPrivacyDraft(value)
    setPrivacyEditing(false)
    try {
      await apiUpdateLegalSettings({ privacy: value })
      toast.success('Privacy policy updated!')
    } catch {
      toast.error('Failed to save privacy policy')
    }
  }

  const saveTerms = async () => {
    if (!termsEditing) {
      setTermsDraft(terms)
      setTermsEditing(true)
      return
    }
    const value = termsDraft.trim() || terms
    setTerms(value)
    setTermsDraft(value)
    setTermsEditing(false)
    try {
      await apiUpdateLegalSettings({ terms: value })
      toast.success('Terms & conditions updated!')
    } catch {
      toast.error('Failed to save terms & conditions')
    }
  }

  const { confirm, showSuccess } = useConfirmDialog()

  const handleLogout = async () => {
    const confirmed = await confirm({
      title: 'Sign Out',
      message: 'Are you sure you want to sign out of the Salzburg Cab dashboard on this device?',
      confirmText: 'Sign Out',
      cancelText: 'Stay Signed In',
      variant: 'warning',
      icon: 'logout',
    })

    if (!confirmed) return

    await showSuccess({
      title: 'Signed Out Successfully',
      message: 'You have been securely signed out. Redirecting to login...',
      autoCloseMs: 1200,
    })

    void logoutAdmin()
    navigate('/admin-login', { replace: true })
  }

  const currentUser = {
    name: `${profile.firstName} ${profile.lastName}`.trim() || 'Admin',
    email: profile.email,
    avatar: photo || shellAssets.avatar,
  }

  return (
    <DashboardLayout
      activeNavId="profile-settings"
      title="Profile Settings"
      subtitle={
        <div className="flex h-[20px] items-center gap-[3px] py-px text-dash-muted">
          <Link className="whitespace-nowrap hover:underline" to={dashboardRoutes.overview}>
            Dashboard
          </Link>
          <img
            alt=""
            aria-hidden
            className="block h-[24px] w-[12px] max-w-none shrink-0 opacity-70"
            src={controlIcons.breadcrumbArrow}
          />
          <span className="whitespace-nowrap">Profile settings</span>
        </div>
      }
      user={currentUser}
      showTopbarUser={false}
      contentGap={27}
    >
    <main
  className="
    flex
    w-full
    flex-col
    gap-[24px]
    pb-[36px]
    pr-[6px]
  "
>
        <section className="rounded-[18px] bg-white px-[24px] py-[22px] sm:px-[28px]">
          <h1 className="font-host text-[20px] font-semibold leading-[1.2] text-black">My Profile</h1>
          <p className="mt-[12px] font-host text-[16px] font-normal leading-[1.2] text-[#8C8C8C]">
            Update your personal details and photo
          </p>

          <div className="mt-[18px] flex items-center gap-[20px]">
            <div className="relative h-[94px] w-[94px] shrink-0 sm:h-[104px] sm:w-[104px]">
              {photo === defaultAvatar ? (
                <img
                  alt="Admin profile"
                  className="h-full w-full object-contain"
                  src={photo}
                />
              ) : (
                <>
                  <img
                    alt="Admin profile"
                    className="h-full w-full rounded-full bg-[#F1F3F7] object-cover"
                    src={photo}
                  />
                  <span className="absolute bottom-[1px] right-[1px] flex size-[28px] items-center justify-center rounded-full border border-[#DADADA] bg-white text-black shadow-sm">
                    <PencilLine className="size-[16px]" strokeWidth={2.2} />
                  </span>
                </>
              )}

              <button
                type="button"
                aria-label="Change profile photo"
                className="absolute bottom-0 right-0 size-[32px] rounded-full bg-transparent"
                onClick={() => fileInputRef.current?.click()}
              />
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handlePhoto}
              />
            </div>

            <div className="min-w-0 pb-[2px]">
              <p className="font-host text-[17px] font-medium leading-[1.4] text-black">
                {profile.firstName}
              </p>
              <p className="mt-[5px] truncate font-host text-[16px] font-normal leading-[1.4] text-black">
                {profile.email}
              </p>
            </div>
          </div>
        </section>

        <section className="rounded-[18px] bg-white px-[24px] py-[22px] sm:px-[28px]">
          <div className="flex items-center justify-between gap-4">
            <h2 className="font-host text-[18px] font-semibold leading-[1.25] text-black">
              Personal Information
            </h2>
            <EditButton editing={profileEditing} loading={loading} onClick={handleProfileEdit} />
          </div>

          <div className="mt-[24px] grid grid-cols-1 gap-x-[36px] gap-y-[24px] md:grid-cols-2">
            <ProfileField
              label="First Name"
              value={draft.firstName}
              editing={profileEditing}
              onChange={(value) => setDraft((current) => ({ ...current, firstName: value }))}
            />
            <ProfileField
              label="Last Name"
              value={draft.lastName}
              editing={profileEditing}
              onChange={(value) => setDraft((current) => ({ ...current, lastName: value }))}
            />
            <ProfileField
              label="Email Address"
              value={draft.email}
              editing={profileEditing}
              onChange={(value) => setDraft((current) => ({ ...current, email: value }))}
              type="email"
            />
            <ProfileField
              label="Phone Number"
              value={draft.phone}
              editing={profileEditing}
              onChange={(value) => setDraft((current) => ({ ...current, phone: value }))}
            />
            <ProfileField
              label="Role"
              value={draft.role}
              editing={false}
              onChange={() => undefined}
            />
            <ProfileField
              label="Country"
              value={draft.country}
              editing={profileEditing}
              onChange={(value) => setDraft((current) => ({ ...current, country: value }))}
            />
          </div>
        </section>

        <section className="rounded-[18px] bg-white px-[24px] py-[24px] sm:px-[28px]">
          <h2 className="font-host text-[18px] font-semibold leading-[1.25] text-black">
            Change Password
          </h2>

          <div className="mt-[28px] grid grid-cols-1 items-end gap-[26px] md:grid-cols-3">
            <PasswordField
              label="Current Password"
              value={currentPassword}
              visible={showCurrent}
              onChange={setCurrentPassword}
              onToggle={() => setShowCurrent((value) => !value)}
            />
            <PasswordField
              label="New Password"
              value={newPassword}
              visible={showNew}
              onChange={setNewPassword}
              onToggle={() => setShowNew((value) => !value)}
            />
            <PasswordField
              label="Confirm Password"
              value={confirmPassword}
              visible={showConfirm}
              onChange={setConfirmPassword}
              onToggle={() => setShowConfirm((value) => !value)}
            />
          </div>

          <div className="mt-[24px] flex justify-end">
            <button
              type="button"
              onClick={updatePassword}
              disabled={passwordLoading}
              className="flex items-center gap-2 rounded-[9px] bg-[#2F8B2F] px-[18px] py-[11px] font-host text-[16px] font-medium leading-[1.2] text-white transition hover:bg-[#287A28] disabled:opacity-50 active:scale-[0.99]"
            >
              {passwordLoading && <Loader2 className="size-4 animate-spin" />}
              {passwordLoading ? 'Updating...' : 'Update Password'}
            </button>
          </div>
        </section>

        <PolicyCard
          heading="Privacy policy"
          label="Privacy Policy"
          editing={privacyEditing}
          value={privacyEditing ? privacyDraft : privacy}
          onChange={setPrivacyDraft}
          onEdit={savePrivacy}
        />

        <PolicyCard
          heading="Terms & Conditions"
          label="Terms & Conditions"
          editing={termsEditing}
          value={termsEditing ? termsDraft : terms}
          onChange={setTermsDraft}
          onEdit={saveTerms}
        />

        {/* Account Session & Logout */}
        <section className="rounded-[18px] bg-white px-[24px] py-[24px] sm:px-[28px] border border-red-100/60 shadow-sm">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <h2 className="font-host text-[18px] font-semibold leading-[1.25] text-red-600">
                Account Session
              </h2>
              <p className="mt-1 text-[13px] font-host text-gray-500">
                Securely sign out of the Salzburg Cab management dashboard on this device.
              </p>
            </div>
            <button
              type="button"
              onClick={handleLogout}
              className="flex items-center justify-center gap-2 rounded-full border border-red-200 bg-red-50 px-6 py-2.5 font-host text-[14px] font-semibold text-red-600 transition hover:bg-red-600 hover:text-white active:scale-95 shadow-sm"
            >
              <LogOut className="h-4 w-4" />
              Sign Out
            </button>
          </div>
        </section>
      </main>
    </DashboardLayout>
  )
}

type EditButtonProps = {
  editing: boolean
  loading?: boolean
  onClick: () => void
}

const EditButton = ({ editing, loading = false, onClick }: EditButtonProps) => (
  <button
    type="button"
    onClick={onClick}
    disabled={loading}
    className="flex shrink-0 items-center gap-[5px] font-host text-[16px] font-normal leading-[1.2] text-black transition hover:opacity-70 disabled:opacity-50"
  >
    {loading ? (
      <Loader2 className="size-[18px] animate-spin text-[#8EAA4D]" />
    ) : (
      <PencilLine className="size-[19px]" strokeWidth={1.8} />
    )}
    {loading ? 'Saving...' : editing ? 'Save' : 'Edit'}
  </button>
)

type ProfileFieldProps = {
  label: string
  value: string
  editing: boolean
  onChange: (value: string) => void
  type?: 'text' | 'email'
}

const ProfileField = ({
  label,
  value,
  editing,
  onChange,
  type = 'text',
}: ProfileFieldProps) => (
  <label className="block">
    <span className="mb-[7px] block font-host text-[16px] font-normal leading-[1.2] text-black">
      {label}
    </span>
    <input
      type={type}
      value={value}
      readOnly={!editing}
      onChange={(event) => onChange(event.target.value)}
      className={`h-[58px] w-full rounded-[9px] border bg-white px-[24px] font-host text-[15px] font-normal text-[#5E5E5E] outline-none transition ${
        editing
          ? 'border-[#8EAA4D] ring-1 ring-transparent focus:ring-[#BAD67E]'
          : 'cursor-default border-[#BEBEBE]'
      }`}
    />
  </label>
)

type PasswordFieldProps = {
  label: string
  value: string
  visible: boolean
  onChange: (value: string) => void
  onToggle: () => void
}

const PasswordField = ({ label, value, visible, onChange, onToggle }: PasswordFieldProps) => (
  <label className="block">
    <span className="mb-[7px] block font-host text-[16px] font-normal leading-[1.2] text-black">
      {label}
    </span>
    <span className="flex h-[58px] items-center rounded-[9px] border border-[#BEBEBE] bg-white px-[24px] focus-within:border-[#8EAA4D]">
      <input
        type={visible ? 'text' : 'password'}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className="min-w-0 flex-1 bg-transparent font-host text-[18px] font-medium tracking-[0.09em] text-black outline-none"
        aria-label={label}
      />
      <button
        type="button"
        onClick={onToggle}
        aria-label={visible ? `Hide ${label}` : `Show ${label}`}
        className="ml-3 flex size-[27px] shrink-0 items-center justify-center text-black transition hover:opacity-60"
      >
        {visible ? <Eye className="size-[22px]" strokeWidth={2.4} /> : <EyeOff className="size-[22px]" strokeWidth={2.4} />}
      </button>
    </span>
  </label>
)

type PolicyCardProps = {
  heading: string
  label: string
  value: string
  editing: boolean
  onChange: (value: string) => void
  onEdit: () => void
}

const PolicyCard = ({ heading, label, value, editing, onChange, onEdit }: PolicyCardProps) => (
  <section className="rounded-[18px] bg-white px-[24px] py-[24px] sm:px-[28px]">
    <div className="flex items-center justify-between gap-4">
      <h2 className="font-host text-[18px] font-semibold leading-[1.25] text-black">{heading}</h2>
      <EditButton editing={editing} onClick={onEdit} />
    </div>

    <label className="mt-[27px] block">
      <span className="mb-[8px] block font-host text-[16px] font-normal leading-[1.2] text-black">
        {label}
      </span>
      <textarea
        value={value}
        readOnly={!editing}
        onChange={(event) => onChange(event.target.value)}
        className={`min-h-[196px] w-full resize-none rounded-[9px] border bg-white px-[26px] py-[22px] font-host text-[16px] font-normal leading-[1.55] text-black outline-none transition ${
          editing
            ? 'border-[#8EAA4D] ring-1 ring-transparent focus:ring-[#BAD67E]'
            : 'cursor-default border-[#BEBEBE]'
        }`}
      />
    </label>
  </section>
)

export default ProfileSettingsPage
